/**
 * 定时器管理工具类
 * 用于统一管理组件中的所有定时器,防止内存泄漏
 *
 * @example
 * // 在组件中使用
 * import TimerManager from '@/utils/TimerManager'
 *
 * export default {
 *   data() {
 *     return {
 *       timerManager: new TimerManager()
 *     }
 *   },
 *   mounted() {
 *     this.timerManager.setInterval('updateData', () => {
 *       // 执行定时任务
 *     }, 1000)
 *   },
 *   beforeDestroy() {
 *     this.timerManager.clearAll()
 *   }
 * }
 */
class TimerManager {
  constructor () {
    // 存储所有的定时器
    this.timers = new Map()
    this.intervals = new Map()
  }

  /**
   * 设置 setTimeout
   * @param {string} name - 定时器名称
   * @param {Function} callback - 回调函数
   * @param {number} delay - 延迟时间(毫秒)
   * @returns {number} - 定时器 ID
   */
  setTimeout (name, callback, delay) {
    // 如果已存在同名定时器,先清除
    this.clearTimeout(name)

    const timerId = setTimeout(() => {
      callback()
      // 执行完成后从 Map 中删除
      this.timers.delete(name)
    }, delay)

    this.timers.set(name, {
      id: timerId,
      type: 'timeout'
    })

    return timerId
  }

  /**
   * 设置 setInterval
   * @param {string} name - 定时器名称
   * @param {Function} callback - 回调函数
   * @param {number} interval - 间隔时间(毫秒)
   * @returns {number} - 定时器 ID
   */
  setInterval (name, callback, interval) {
    // 如果已存在同名定时器,先清除
    this.clearInterval(name)

    const timerId = setInterval(callback, interval)

    this.intervals.set(name, {
      id: timerId,
      type: 'interval'
    })

    return timerId
  }

  /**
   * 清除指定的 setTimeout
   * @param {string} name - 定时器名称
   * @returns {boolean} - 是否成功清除
   */
  clearTimeout (name) {
    const timer = this.timers.get(name)
    if (timer) {
      clearTimeout(timer.id)
      this.timers.delete(name)
      return true
    }
    return false
  }

  /**
   * 清除指定的 setInterval
   * @param {string} name - 定时器名称
   * @returns {boolean} - 是否成功清除
   */
  clearInterval (name) {
    const timer = this.intervals.get(name)
    if (timer) {
      clearInterval(timer.id)
      this.intervals.delete(name)
      return true
    }
    return false
  }

  /**
   * 清除指定名称的定时器(自动判断类型)
   * @param {string} name - 定时器名称
   * @returns {boolean} - 是否成功清除
   */
  clear (name) {
    return this.clearTimeout(name) || this.clearInterval(name)
  }

  /**
   * 清除所有定时器
   */
  clearAll () {
    // 清除所有 timeout
    this.timers.forEach((timer) => {
      clearTimeout(timer.id)
    })
    this.timers.clear()

    // 清除所有 interval
    this.intervals.forEach((timer) => {
      clearInterval(timer.id)
    })
    this.intervals.clear()
  }

  /**
   * 检查指定定时器是否存在
   * @param {string} name - 定时器名称
   * @returns {boolean}
   */
  has (name) {
    return this.timers.has(name) || this.intervals.has(name)
  }

  /**
   * 获取所有定时器的名称列表
   * @returns {Array<string>}
   */
  getTimerNames () {
    return [
      ...Array.from(this.timers.keys()),
      ...Array.from(this.intervals.keys())
    ]
  }

  /**
   * 获取定时器数量
   * @returns {Object} - { timeout: number, interval: number, total: number }
   */
  getTimerCount () {
    return {
      timeout: this.timers.size,
      interval: this.intervals.size,
      total: this.timers.size + this.intervals.size
    }
  }

  /**
   * 暂停指定的 interval(通过清除并返回配置)
   * 注意: setTimeout 无法暂停
   * @param {string} name - 定时器名称
   * @returns {Object|null} - 定时器配置信息
   */
  pause (name) {
    const timer = this.intervals.get(name)
    if (timer) {
      clearInterval(timer.id)
      return {
        name,
        ...timer
      }
    }
    return null
  }

  /**
   * 恢复暂停的 interval
   * @param {string} name - 定时器名称
   * @param {Function} callback - 回调函数
   * @param {number} interval - 间隔时间
   */
  resume (name, callback, interval) {
    return this.setInterval(name, callback, interval)
  }
}

export default TimerManager
